import { Button, ButtonProps, Stack, StackProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import { useUpdateEffect } from "react-use";

interface Props {
  disabled?: boolean;
  disabledCancel?: boolean;
  disabledSubmit?: boolean;
  onCancel?: () => void;
  onChange?: (values: any) => void;
  onSubmitted?: () => void;
  onSubmit?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  form?: string;
  stackProps?: StackProps;
  saveButtonTitle?: string;
  cancelButtonStyle?: React.CSSProperties;
  submitButtonStyle?: React.CSSProperties;
  buttonSize?: ButtonProps["size"];
  buttonLayout?: "full-width" | "inline";
}

export const ManualSave = ({
  disabledCancel = false,
  disabledSubmit = false,
  disabled = false,
  onCancel,
  onChange,
  onSubmitted,
  onSubmit,
  form,
  stackProps,
  saveButtonTitle,
  cancelButtonStyle,
  submitButtonStyle,
  buttonSize = "small",
  buttonLayout = "full-width",
}: Props) => {
  const { t } = useTranslate("Global");
  const { isValid, isSubmitting, dirty, values } = useFormikContext();

  useUpdateEffect(() => {
    onChange?.(values);
  }, [values]);

  useUpdateEffect(() => {
    if (!isSubmitting) onSubmitted?.();
  }, [isSubmitting]);

  const isInline = buttonLayout === "inline";

  return (
    <Stack
      direction="row"
      spacing={1}
      {...stackProps}
      sx={[
        {
          width: "100%",
          justifyContent: isInline ? "flex-end" : "stretch",
        },
        ...(Array.isArray(stackProps?.sx) ? stackProps?.sx : [stackProps?.sx]),
      ]}
    >
      <Button
        disabled={disabled || disabledCancel}
        fullWidth={!isInline}
        onClick={onCancel}
        size={buttonSize}
        variant="outlined"
        color="primary"
        style={cancelButtonStyle}
        sx={isInline ? { flex: { xs: 1, md: 0 } } : { flex: 1 }}
      >
        {t("Cancel")}
      </Button>
      <Button
        fullWidth={!isInline}
        disabled={
          disabled || disabledSubmit || !dirty || !isValid || isSubmitting
        }
        type="submit"
        size={buttonSize}
        variant="contained"
        color="primary"
        form={form}
        onClick={onSubmit}
        style={submitButtonStyle}
        sx={isInline ? { flex: { xs: 1, md: 0 } } : { flex: 1 }}
      >
        {saveButtonTitle ?? t("Save changes")}
      </Button>
    </Stack>
  );
};
